/**
 * Lab bookings — home sample collection and lab visits (the patient comes to the lab at a booked slot):
 * clinic settings, the staff board, and the patient booking flow.
 * The clinic is always the active clinic (staff) or the patient's token clinic — never sent by us.
 */
import { fetchApi, fetchPatientApi } from '../client';
import type { PagedResponse } from '../../../types/pagination';

export type HomeCollectionStatus =
  | 'REQUESTED' | 'CONFIRMED' | 'ASSIGNED' | 'CHECKED_IN' | 'COLLECTED' | 'RECEIVED_AT_LAB' | 'REPORTED' | 'CANCELLED' | 'NO_SHOW';

/** HOME: collected at the patient's address. LAB_VISIT: the patient comes to the lab at a booked slot. */
export type VisitMode = 'HOME' | 'LAB_VISIT';

export interface HcWindow { id: string; startsAt: string; endsAt: string; capacity: number; active: boolean }

export interface HcSettings {
  entitled: boolean;
  enabled: boolean;
  collectionFee: number;
  gstPercent: number;
  pincodes: string[];
  minLeadHours: number;
  maxDaysAhead: number;
  patientInstructions?: string | null;
  windows: HcWindow[];
  problems: string[];
  labVisit: HcLabVisitSettings;
}

export interface HcLabVisitSettings {
  entitled: boolean;
  enabled: boolean;
  minLeadHours: number;
  instructions?: string | null;
  windows: HcWindow[];
  problems: string[];
}

export interface HcSettingsInput {
  enabled?: boolean;
  collectionFee?: number;
  gstPercent?: number;
  pincodes?: string[];
  minLeadHours?: number;
  maxDaysAhead?: number;
  patientInstructions?: string;
  windows?: Array<{ id?: string; startsAt: string; endsAt: string; capacity: number; active: boolean }>;
  labVisitEnabled?: boolean;
  labVisitMinLeadHours?: number;
  labVisitInstructions?: string;
  labVisitWindows?: Array<{ id?: string; startsAt: string; endsAt: string; capacity: number; active: boolean }>;
}

export interface HcItem { id: string; testName: string; price: number; labOrderId?: string | null }
/** paymentStatus FINALIZED with paidAmount > 0 is part-paid. */
export interface HcBill { billId: string; billNumber: string; total: number; paymentStatus: string; paidAmount?: number | null }

export interface HcStaffBooking {
  id: string;
  bookingNumber: string;
  status: HomeCollectionStatus;
  source: 'CATALOGUE' | 'DOCTOR_ORDER';
  date: string;
  windowId: string;
  window: string;
  pincode?: string | null;
  patientName?: string | null;
  patientPhone?: string | null;
  address?: string | null;
  landmark?: string | null;
  notes?: string | null;
  items: HcItem[];
  collectionFee: number;
  accessionId?: string | null;
  collectorUserId?: string | null;
  collectorName?: string | null;
  orderingDoctorId?: string | null;
  bill?: HcBill | null;
  missedAttempts?: number;
  lastMissedReason?: string | null;
  createdAt?: string;
  visitMode?: VisitMode;
  /** PATIENT_APP or PUBLIC_WEB */
  channel?: string;
  checkedInAt?: string | null;
  /** At the lab with every result released, but the doctor hasn't reviewed and shared them (medopd-docs 104). */
  withDoctor?: boolean;
}

export interface HcBoard {
  date: string;
  byStatus: Record<HomeCollectionStatus, number>;
  bookings: HcStaffBooking[];
  /** A self-registered lab (free lab listing): no ordering doctor, lab orders or bill; staff record collection and report here. */
  bookingOnly?: boolean;
}
export interface HcCollector { userId: string; name: string; role: string }

// Patient side
export interface HcSlot { windowId: string; label: string; remaining: number; available: boolean; reason?: string | null }
export interface HcAvailability {
  offered: boolean;
  serviceable: boolean;
  message?: string | null;
  collectionFee?: number | null;
  instructions?: string | null;
  earliestDate?: string | null;
  latestDate?: string | null;
  slots: HcSlot[];
  visitMode?: VisitMode;
  labAddress?: string | null;
}
export interface HcModes { homeCollection: boolean; labVisit: boolean }
export interface HcCatalogueTest { id: string; name: string; sampleType?: string | null; price: number }
export interface HcEligibleOrder { labOrderId: string; accessionId: string; testName: string; orderedAt?: string; doctorName?: string | null }
export interface HcBookingRequest {
  source: 'CATALOGUE' | 'DOCTOR_ORDER';
  testDefinitionIds?: string[];
  labOrderIds?: string[];
  date: string;
  windowId: string;
  /** Home collection only. */
  address?: { line: string; landmark?: string; pincode: string };
  contactMobile?: string;
  notes?: string;
  visitMode?: VisitMode;
}
export interface HcPatientBooking {
  id: string;
  bookingNumber: string;
  status: HomeCollectionStatus;
  source: 'CATALOGUE' | 'DOCTOR_ORDER';
  date: string;
  window: string;
  pincode?: string | null;
  items: HcItem[];
  collectionFee: number;
  bill?: HcBill | null;
  cancellable: boolean;
  createdAt?: string;
  visitMode?: VisitMode;
}

const post = (body?: unknown): RequestInit => ({ method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) });
const qs = (params: Record<string, string | undefined>) =>
  new URLSearchParams(Object.entries(params).filter(([, v]) => v) as [string, string][]).toString();

export const homeCollectionModule = {
  settings: {
    get: () => fetchApi<HcSettings>('/api/home-collection/settings'),
    update: (data: HcSettingsInput) => fetchApi<HcSettings>('/api/home-collection/settings', { method: 'PUT', body: JSON.stringify(data) }),
  },
  board: (date?: string) => fetchApi<HcBoard>(`/api/home-collection/bookings?${qs({ date })}`),
  myAssignments: (date?: string) => fetchApi<HcStaffBooking[]>(`/api/home-collection/my-assignments?${qs({ date })}`),
  collectors: () => fetchApi<HcCollector[]>('/api/home-collection/collectors'),
  confirm: (id: string, orderingDoctorId?: string, itemPrices?: Record<string, number>) =>
    fetchApi<HcStaffBooking>(`/api/home-collection/bookings/${encodeURIComponent(id)}/confirm`, post({ orderingDoctorId, itemPrices })),
  assign: (id: string, collectorUserId: string, date?: string, windowId?: string) =>
    fetchApi<HcStaffBooking>(`/api/home-collection/bookings/${encodeURIComponent(id)}/assign`, post({ collectorUserId, date, windowId })),
  cancel: (id: string, reason: string) =>
    fetchApi<HcStaffBooking>(`/api/home-collection/bookings/${encodeURIComponent(id)}/cancel`, post({ reason })),
  /** Collector visited but couldn't collect: back to Confirmed, collector cleared, reason kept. */
  missed: (id: string, reason: string) =>
    fetchApi<HcStaffBooking>(`/api/home-collection/bookings/${encodeURIComponent(id)}/missed`, post({ reason })),
  /** Lab visit: the patient has arrived at the desk. */
  checkIn: (id: string) => fetchApi<HcStaffBooking>(`/api/home-collection/bookings/${encodeURIComponent(id)}/check-in`, post()),
  /** Lab visit: the patient didn't come (only after the slot ended). */
  noShow: (id: string, reason?: string) =>
    fetchApi<HcStaffBooking>(`/api/home-collection/bookings/${encodeURIComponent(id)}/no-show`, post({ reason })),
  /** Booking-only labs: the sample was taken (home: confirmed/assigned; lab visit: checked in), on the day. */
  markCollected: (id: string) => fetchApi<HcStaffBooking>(`/api/home-collection/bookings/${encodeURIComponent(id)}/collected`, post()),
  /** Booking-only labs: the report was given to the patient. */
  markReported: (id: string) => fetchApi<HcStaffBooking>(`/api/home-collection/bookings/${encodeURIComponent(id)}/reported`, post()),
};

/** Patient portal calls (patient token). */
export const patientHomeCollectionModule = {
  modes: () => fetchPatientApi<HcModes>('/api/patient/home-collection/modes'),
  availability: (pincode?: string, date?: string, mode: VisitMode = 'HOME') =>
    fetchPatientApi<HcAvailability>(`/api/patient/home-collection/availability?${qs({ pincode, date, mode })}`),
  catalogue: (q?: string, mode: VisitMode = 'HOME') =>
    fetchPatientApi<HcCatalogueTest[]>(`/api/patient/home-collection/catalogue?${qs({ q, mode })}`),
  eligibleOrders: (mode: VisitMode = 'HOME') =>
    fetchPatientApi<HcEligibleOrder[]>(`/api/patient/home-collection/eligible-orders?${qs({ mode })}`),
  bookings: () => fetchPatientApi<HcPatientBooking[]>('/api/patient/home-collection/bookings'),
  /** One page of the patient's bookings, newest first (medopd-docs 117). */
  bookingsPage: (page: number, size: number) =>
    fetchPatientApi<PagedResponse<HcPatientBooking>>(`/api/patient/home-collection/bookings?page=${page}&size=${size}`),
  book: (data: HcBookingRequest) => fetchPatientApi<HcPatientBooking>('/api/patient/home-collection/bookings', post(data)),
  cancel: (id: string, reason?: string) =>
    fetchPatientApi<HcPatientBooking>(`/api/patient/home-collection/bookings/${encodeURIComponent(id)}/cancel`, post({ reason })),
};
